// src/pages/Profile/LocationForm.jsx
import React, { useMemo, useState, useCallback, useId, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { collection, serverTimestamp, addDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { GTA_GAME_TABS as GAMES } from "../Community/components/ToolbarHeader";

const CATEGORIES = [
  "Hidden Location",
  "Secret Spots",
  "Collectibles",
  "Stunt Spots",
  "Safehouses",
  "Shops & Garages",
  "Landmarks",
  "Other",
];

const DEFAULT_GAME_MAP_IMG = "/assets/gta6-map.jpg";
const labelByKey = (k) => GAMES.find((g) => g.key === k)?.label || "Other";

const MAX_IMAGE_BYTES = 6 * 1024 * 1024; // 6 MB
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

const toSlug = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

export default function LocationForm({
  user,
  role,
  onDone,
  onCancel,
  defaultGameKey = "gta-6",
}) {
  const isAuthed = Boolean(user?.uid);
  const isAdmin = ["admin", "superadmin"].includes(
    String(user?.role || role || "").toLowerCase()
  );
  const canCreate = isAuthed;
  const storage = getStorage();

  const [banner, setBanner] = useState({ type: null, text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [previewDesc, setPreviewDesc] = useState(false);
  const [issues, setIssues] = useState([]);

  const [form, setForm] = useState({
    // game
    gameKey: defaultGameKey,
    game: labelByKey(defaultGameKey),

    // basics
    title: "",
    category: "Hidden Location",
    city: "",
    region: "",

    // rich text
    description: "",

    // meta
    tagsCsv: "",
    coverFile: null,
    coverUrl: "",
    images: [""], // gallery URLs

    shortNote: "",

    // map pin
    mapImage: "",
    xPct: "",
    yPct: "",

    // discovery info
    isPrivate: true,
    difficultyToFind: "Medium",
    accessReq: "",
    lootHint: "",
  });

  const updateField = useCallback((k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
  }, []);

  const onChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (name === "coverFile") {
      const file = files?.[0] || null;
      if (file) {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          toast.error("Unsupported image type. Use JPG/PNG/WebP/GIF.");
          return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          toast.error("Image too large. Max 6 MB.");
          return;
        }
      }
      setForm((f) => ({ ...f, coverFile: file }));
      return;
    }

    if (name === "gameKey") {
      setForm((f) => ({ ...f, gameKey: value, game: labelByKey(value) }));
      return;
    }

    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
      return;
    }

    // numeric fields: accept blank string or parse to number for state
    if (name === "xPct" || name === "yPct") {
      setForm((f) => ({ ...f, [name]: value }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const updateImageRow = (idx, v) =>
    setForm((s) => {
      const next = [...s.images];
      next[idx] = v;
      return { ...s, images: next };
    });

  const addImageRow = () =>
    setForm((s) => ({ ...s, images: [...s.images, ""] }));

  const removeImageRow = (idx) =>
    setForm((s) => {
      const next = s.images.filter((_, i) => i !== idx);
      return { ...s, images: next.length ? next : [""] };
    });

  const fillDemoLocation = () => {
    setForm((f) => ({
      ...f,
      title: "Hidden Rooftop Stash",
      gameKey: "gta-6",
      game: "GTA VI",
      category: "Hidden Location",
      city: "Vice City",
      region: "Downtown",
      description:
        "A tucked-away rooftop with a stash crate. **Come after midnight** to avoid patrols.\n\n- Use fire escape behind the donut shop\n- Watch for camera above the stairwell\n- Bonus loot spawns on rainy nights",
      tagsCsv: "hidden, stash, rooftop, vice-city",
      shortNote: "Break the vent to access stash",
      mapImage: "",
      xPct: "62.5",
      yPct: "37.4",
      isPrivate: true,
      difficultyToFind: "Medium",
      accessReq: "None",
      lootHint: "Cash stash + rare armor",
    }));
  };

  // ---------- validation ----------
  const issuesComputed = useMemo(() => {
    const errs = [];
    if (!isAuthed) errs.push("You must be signed in.");
    if (!form.title.trim()) errs.push("Title is required.");
    if (!CATEGORIES.includes(form.category)) errs.push("Invalid category.");

    const urlLike = (v) => {
      if (!v?.trim()) return true;
      try {
        new URL(v.trim());
        return true;
      } catch {
        return false;
      }
    };

    if (form.coverUrl && !urlLike(form.coverUrl))
      errs.push("Cover Image URL is invalid.");
    if (form.mapImage && !urlLike(form.mapImage))
      errs.push("Map Image URL is invalid.");

    form.images.forEach((u, i) => {
      if (u.trim() && !urlLike(u))
        errs.push(`Gallery URL #${i + 1} is invalid.`);
    });

    const hasX = String(form.xPct).trim() !== "";
    const hasY = String(form.yPct).trim() !== "";
    if (hasX ^ hasY) errs.push("Provide both X% and Y% for the map pin.");

    const nX = Number(form.xPct);
    const nY = Number(form.yPct);
    if (hasX && (Number.isNaN(nX) || nX < 0 || nX > 100))
      errs.push("Map X% must be a number between 0 and 100.");
    if (hasY && (Number.isNaN(nY) || nY < 0 || nY > 100))
      errs.push("Map Y% must be a number between 0 and 100.");

    if (form.coverFile) {
      if (!ALLOWED_IMAGE_TYPES.includes(form.coverFile.type))
        errs.push("Cover upload type must be JPG/PNG/WebP/GIF.");
      if (form.coverFile.size > MAX_IMAGE_BYTES)
        errs.push("Cover upload exceeds 6 MB.");
    }

    return errs;
  }, [form, isAuthed]);

  // keep issues live as user types
  useEffect(() => setIssues(issuesComputed), [issuesComputed]);

  const canSubmit = issuesComputed.length === 0 && !submitting;

  // ---------- uploads ----------
  const uploadCoverIfAny = async () => {
    if (!form.coverFile || !user?.uid) return form.coverUrl?.trim() || "";
    const file = form.coverFile;
    const safeName = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `locations/${user.uid}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  // ---------- submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setBanner({ type: null, text: "" });

    try {
      const coverUrl = await uploadCoverIfAny();

      // normalize tags
      const tags = form.tagsCsv
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      // gallery (urls only)
      const gallery = Array.from(
        new Set(form.images.map((u) => u.trim()).filter(Boolean))
      );

      // coords
      const hasX = String(form.xPct).trim() !== "";
      const hasY = String(form.yPct).trim() !== "";
      const coordsPct =
        hasX && hasY
          ? {
              xPct: Number.parseFloat(form.xPct),
              yPct: Number.parseFloat(form.yPct),
            }
          : null;

      const payload = {
        // game
        gameKey: form.gameKey,
        game: form.game,

        // basics
        title: form.title.trim(),
        slug: toSlug(form.title),
        category: form.category,
        city: form.city.trim() || null,
        region: form.region.trim() || null,

        // content/meta
        description: form.description.trim(), // markdown
        tags,
        coverUrl: coverUrl || null,
        images: gallery,
        shortNote: form.shortNote.trim() || null,

        // map
        mapImage: form.mapImage.trim() || null,
        coordsPct,

        // discovery
        isPrivate: !!form.isPrivate,
        difficultyToFind: form.difficultyToFind || "Medium",
        accessReq: form.accessReq.trim() || null,
        lootHint: form.lootHint.trim() || null,

        // audit
        upvotes: 0,
        authorUid: user.uid,
        authorEmail: user.email || null,
        authorName: user.displayName || "Anonymous",
        roleAtCreation: String(user?.role || role || "").toLowerCase() || null,
        // Back-compat:
        createdBy: user.uid,
        createdByUid: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "locations"), payload);

      setBanner({ type: "ok", text: "Location saved successfully." });
      toast.success("Location saved successfully");

      // reset (keep selected game)
      setForm((s) => ({
        ...s,
        title: "",
        category: "Hidden Location",
        city: "",
        region: "",
        description: "",
        tagsCsv: "",
        coverFile: null,
        coverUrl: "",
        images: [""],
        shortNote: "",
        mapImage: "",
        xPct: "",
        yPct: "",
        isPrivate: true,
        difficultyToFind: "Medium",
        accessReq: "",
        lootHint: "",
      }));
      setPreviewDesc(false);
      onDone?.();
    } catch (err) {
      console.error("[locations:addDoc] error:", err);
      setBanner({ type: "error", text: "Save failed. Try again." });
      toast.error("Save failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const previewTags = useMemo(
    () =>
      form.tagsCsv
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [form.tagsCsv]
  );

  const mapPreviewSrc = form.mapImage?.trim() || DEFAULT_GAME_MAP_IMG;
  const showPin =
    String(form.xPct).trim() !== "" && String(form.yPct).trim() !== "";

  const mapImgId = useId();

  return (
    <>
      {/* Banner */}
      {banner.type && (
        <div
          className={
            "mt6-banner " +
            (banner.type === "ok"
              ? "mt6-banner--ok"
              : banner.type === "warn"
              ? "mt6-banner--warn"
              : "mt6-banner--error")
          }
          role="status"
        >
          {banner.text}
        </div>
      )}

      {/* Issues */}
      {issues.length > 0 && (
        <ul className="mt6-issues" role="alert" aria-live="assertive">
          {issues.map((m, i) => (
            <li key={i}>• {m}</li>
          ))}
        </ul>
      )}

      <form onSubmit={onSubmit} className="mt6-form">
        {/* ---- Basics ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Basics</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label" htmlFor="lf-title">
                Title*
              </label>
              <input
                id="lf-title"
                className="mt6-input"
                name="title"
                value={form.title}
                onChange={onChange}
                required
                placeholder="e.g., Hidden Rooftop Stash"
                disabled={!canCreate || submitting}
              />
            </div>

            <div>
              <label className="mt6-label" htmlFor="lf-game">
                Game*
              </label>
              <select
                id="lf-game"
                className="mt6-input"
                name="gameKey"
                value={form.gameKey}
                onChange={onChange}
                required
                disabled={!canCreate || submitting}
              >
                {GAMES.filter((g) => g.key !== "all").map(({ key, label }) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {/* Visibility */}
            <div>
              <label className="mt6-label">Visibility</label>
              <div
                className="mt6-segment"
                role="tablist"
                aria-label="Visibility"
              >
                <button
                  type="button"
                  className={`mt6-seg-btn ${!form.isPrivate ? "is-active" : ""}`}
                  onClick={() => updateField("isPrivate", false)}
                  aria-pressed={!form.isPrivate}
                >
                  Public
                </button>
                <button
                  type="button"
                  className={`mt6-seg-btn ${form.isPrivate ? "is-active" : ""}`}
                  onClick={() => updateField("isPrivate", true)}
                  aria-pressed={form.isPrivate}
                >
                  Private
                </button>
              </div>

              <div
                className="mt6-muted"
                aria-live="polite"
                style={{ marginTop: 6 }}
              >
                {form.isPrivate ? "Private: visible only to you." : "Public: visible to everyone."}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="mt6-label">Category</label>
          <div className="mt6-pills" role="tablist" aria-label="Category">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={`mt6-pill ${form.category === c ? "is-active" : ""}`}
                onClick={() => updateField("category", c)}
                disabled={!canCreate || submitting}
                aria-pressed={form.category === c}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Details ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Details</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label" htmlFor="lf-city">
                City (optional)
              </label>
              <input
                id="lf-city"
                className="mt6-input"
                name="city"
                value={form.city}
                onChange={onChange}
                placeholder="e.g., Vice City"
                disabled={!canCreate || submitting}
              />
            </div>

            <div>
              <label className="mt6-label" htmlFor="lf-region">
                Region (optional)
              </label>
              <input
                id="lf-region"
                className="mt6-input"
                name="region"
                value={form.region}
                onChange={onChange}
                placeholder="e.g., Downtown"
                disabled={!canCreate || submitting}
              />
            </div>
          </div>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label" htmlFor="lf-diff">
                Difficulty to find
              </label>
              <select
                id="lf-diff"
                className="mt6-input"
                name="difficultyToFind"
                value={form.difficultyToFind}
                onChange={onChange}
                disabled={!canCreate || submitting}
              >
                {["Easy", "Medium", "Hard"].map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mt6-label" htmlFor="lf-access">
                Access Requirements
              </label>
              <input
                id="lf-access"
                className="mt6-input"
                name="accessReq"
                value={form.accessReq}
                onChange={onChange}
                placeholder="e.g., Finish mission X, or use a helicopter"
                disabled={!canCreate || submitting}
              />
            </div>

            <div>
              <label className="mt6-label" htmlFor="lf-loot">
                Loot / Reward Hint
              </label>
              <input
                id="lf-loot"
                className="mt6-input"
                name="lootHint"
                value={form.lootHint}
                onChange={onChange}
                placeholder="e.g., Rare armor spawn, cash stash, unique car"
                disabled={!canCreate || submitting}
              />
            </div>
          </div>

          <label className="mt6-label" htmlFor="lf-note">
            Short Note (tooltip)
          </label>
          <input
            id="lf-note"
            className="mt6-input"
            name="shortNote"
            value={form.shortNote}
            onChange={onChange}
            placeholder="e.g., Break the vent to access stash"
            disabled={!canCreate || submitting}
          />
        </div>

        {/* ---- Content ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Content</h4>

          <div className="mt6-segment" role="tablist" aria-label="Content mode">
            <button
              type="button"
              className={`mt6-seg-btn ${!previewDesc ? "is-active" : ""}`}
              onClick={() => setPreviewDesc(false)}
              disabled={submitting}
              aria-pressed={!previewDesc}
            >
              Write
            </button>
            <button
              type="button"
              className={`mt6-seg-btn ${previewDesc ? "is-active" : ""}`}
              onClick={() => setPreviewDesc(true)}
              disabled={submitting}
              aria-pressed={previewDesc}
            >
              Preview
            </button>
          </div>

          {!previewDesc ? (
            <textarea
              className="mt6-input mt6-input--textarea"
              name="description"
              value={form.description}
              onChange={onChange}
              rows={6}
              placeholder="Markdown supported. Add routes, time of day, NPC patterns, hazards, etc."
              disabled={!canCreate || submitting}
            />
          ) : (
            <div className="mt6-preview">
              {form.description.trim() ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                >
                  {form.description}
                </ReactMarkdown>
              ) : (
                <span className="mt6-muted">Nothing to preview.</span>
              )}
            </div>
          )}
        </div>

        {/* ---- Metadata ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Metadata</h4>

          <label className="mt6-label" htmlFor="lf-tags">
            Tags (CSV)
          </label>
          <input
            id="lf-tags"
            className="mt6-input"
            name="tagsCsv"
            value={form.tagsCsv}
            onChange={onChange}
            placeholder="hidden, collectible, stunt"
            disabled={!canCreate || submitting}
          />

          {previewTags.length > 0 && (
            <div className="mt6-pills" style={{ marginTop: 8 }}>
              {previewTags.map((t) => (
                <span key={t} className="mt6-pill is-active">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ---- Media ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Media</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label" htmlFor="lf-cover-file">
                Cover Image (upload)
              </label>
              <input
                id="lf-cover-file"
                type="file"
                name="coverFile"
                accept={ALLOWED_IMAGE_TYPES.join(",")}
                onChange={onChange}
                className="mt6-input"
                disabled={!canCreate || submitting}
              />
            </div>

            <div>
              <label className="mt6-label" htmlFor="lf-cover-url">
                Cover Image URL (optional)
              </label>
              <input
                id="lf-cover-url"
                className="mt6-input"
                name="coverUrl"
                value={form.coverUrl}
                onChange={onChange}
                placeholder="https://…"
                disabled={!canCreate || submitting}
              />
            </div>

            <div />
          </div>

          <label className="mt6-label">Gallery URLs</label>
          {form.images.map((u, i) => (
            <div className="mt6-row" key={`g-${i}`}>
              <input
                className="mt6-input"
                value={u}
                onChange={(e) => updateImageRow(i, e.target.value)}
                placeholder={`Image URL #${i + 1}`}
                disabled={!canCreate || submitting}
              />
              <button
                type="button"
                className="mt6-remove"
                onClick={() => removeImageRow(i)}
                disabled={!canCreate || submitting || form.images.length <= 1}
                aria-label={`Remove image URL ${i + 1}`}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="mt6-add"
            onClick={addImageRow}
            disabled={!canCreate || submitting}
          >
            ＋ Add Image
          </button>
        </div>

        {/* ---- Game Map Pin ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Game Map Pin</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label" htmlFor="lf-mapimg">
                Map Image URL (optional)
              </label>
              <input
                id="lf-mapimg"
                className="mt6-input"
                name="mapImage"
                value={form.mapImage}
                onChange={onChange}
                placeholder="https://… (override default GTA map image)"
                disabled={!canCreate || submitting}
              />
            </div>
            <div>
              <label className="mt6-label" htmlFor="lf-xpct">
                Map X% (0–100)
              </label>
              <input
                id="lf-xpct"
                type="number"
                className="mt6-input"
                name="xPct"
                value={form.xPct}
                onChange={onChange}
                placeholder="e.g., 62.5"
                min={0}
                max={100}
                step="0.1"
                disabled={!canCreate || submitting}
              />
            </div>
            <div>
              <label className="mt6-label" htmlFor="lf-ypct">
                Map Y% (0–100)
              </label>
              <input
                id="lf-ypct"
                type="number"
                className="mt6-input"
                name="yPct"
                value={form.yPct}
                onChange={onChange}
                placeholder="e.g., 37.4"
                min={0}
                max={100}
                step="0.1"
                disabled={!canCreate || submitting}
              />
            </div>
          </div>

          <div className="mt6-mapbox">
            <div className="mt6-mapimg-wrap">
              <img
                id={mapImgId}
                src={mapPreviewSrc}
                alt="Map preview"
                className="mt6-mapimg"
                onClick={(e) => {
                  if (!canCreate || submitting) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  updateField("xPct", x.toFixed(2));
                  updateField("yPct", y.toFixed(2));
                }}
                title="Click to place pin"
              />
              {showPin && (
                <div
                  className="mt6-mappin"
                  style={{
                    left: `${Number(form.xPct)}%`,
                    top: `${Number(form.yPct)}%`,
                  }}
                  title={form.shortNote || form.title || "Location"}
                  aria-label={`Pin at X ${form.xPct}%, Y ${form.yPct}%`}
                >
                  <span className="mt6-mappin-dot" />
                  <span className="mt6-mappin-pulse" />
                </div>
              )}
            </div>
            <div className="mt6-maplegend">
              <code>
                X {String(form.xPct || "—")}%, Y {String(form.yPct || "—")}%
              </code>
              {form.shortNote ? (
                <span className="mt6-legend-note"> • {form.shortNote}</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* ---- Preview ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Preview</h4>
          <div className="mt6-preview">
            <div className="mt6-muted">Location</div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              {form.title || "—"} <span>• {form.game}</span>
            </div>

            {(form.city ||
              form.region ||
              form.difficultyToFind ||
              form.shortNote) && (
              <div style={{ marginTop: 6 }}>
                {form.city && (
                  <div>
                    <strong>City:</strong> {form.city}
                  </div>
                )}
                {form.region && (
                  <div>
                    <strong>Region:</strong> {form.region}
                  </div>
                )}
                {form.difficultyToFind && (
                  <div>
                    <strong>Difficulty:</strong> {form.difficultyToFind}
                  </div>
                )}
                {form.shortNote && (
                  <div>
                    <strong>Note:</strong> {form.shortNote}
                  </div>
                )}
              </div>
            )}

            {previewTags.length > 0 && (
              <div className="mt6-pills" style={{ marginTop: 8 }}>
                {previewTags.map((t) => (
                  <span key={t} className="mt6-pill is-active">
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ---- Actions ---- */}
        <div className="mt6-actions">
          {/* <button
            type="button"
            className="mt6-btn mt6-btn--ghost"
            onClick={fillDemoLocation}
            disabled={submitting}
          >
            Fill Demo Location
          </button> */}

          <button
            className="mt6-btn mt6-btn--primary"
            type="submit"
            disabled={!canSubmit}
            aria-busy={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>

          {onCancel && (
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </>
  );
}
